import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const fmt = (v) => v || "-";
const fmtKg = (n) => new Intl.NumberFormat("es-AR", { maximumFractionDigits: 3 }).format(n || 0) + " kg";

export default function RECDetalleDialog({ open, onOpenChange, rec }) {
  if (!rec) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rec-detalle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>REC {rec.numero_formateado}</span>
            {rec.anulado ? (
              <Badge variant="destructive">Anulado</Badge>
            ) : (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                {rec.estado || "AUTORIZADO"}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 text-sm">
          <section>
            <h3 className="text-xs uppercase text-neutral-500 mb-2 tracking-widest">Emisión</h3>
            <div className="space-y-1">
              <Row k="Especie" v={rec.especie} className="capitalize" />
              <Row k="Tipo comprobante AFIP" v={rec.tipo_cbte} />
              <Row k="Movimiento" v={rec.tipo_movimiento} />
              <Row k="Fecha emisión" v={rec.fecha_emision} />
              <Row k="Vencimiento" v={rec.fecha_vencimiento} />
              <Row k="Código autorización" v={rec.cod_autorizacion} />
              <Row k="Código remito" v={rec.cod_remito} className="font-mono text-xs" />
              <Row k="Cod. domicilio origen" v={rec.cod_dom_origen} />
            </div>
          </section>
          <section>
            <h3 className="text-xs uppercase text-neutral-500 mb-2 tracking-widest">Receptor</h3>
            <div className="space-y-1">
              <Row k="Cliente" v={rec.cliente_nombre} />
              <Row k="CUIT receptor" v={rec.cuit_receptor} />
              <Row k="Cod. domicilio destino" v={rec.cod_dom_destino} />
            </div>
            <h3 className="text-xs uppercase text-neutral-500 mb-2 tracking-widest mt-4">Transporte</h3>
            <div className="space-y-1">
              <Row k="Transportista" v={rec.transportista_nombre} />
              <Row k="CUIT transportista" v={rec.cuit_transportista} />
              <Row k="CUIT conductor" v={rec.cuit_conductor} />
              <Row k="Dominio" v={rec.dominio_vehiculo} className="font-mono uppercase" />
              <Row k="Acoplado" v={rec.dominio_acoplado} className="font-mono uppercase" />
              <Row k="Fecha inicio viaje" v={rec.fecha_inicio_viaje} />
              <Row k="Distancia" v={rec.distancia_km ? `${rec.distancia_km} km` : "-"} />
            </div>
          </section>
        </div>

        <section className="mt-6">
          <h3 className="text-xs uppercase text-neutral-500 mb-2 tracking-widest">Mercadería</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="text-xs uppercase text-neutral-500 border-b">
                <tr>
                  <th className="text-left py-2 px-2">#</th>
                  <th className="text-left px-2">Cód. producto</th>
                  <th className="text-left px-2">Descripción</th>
                  <th className="text-right px-2">Kilos</th>
                  <th className="text-right px-2">Unidades</th>
                  <th className="text-right px-2">Tropa</th>
                </tr>
              </thead>
              <tbody>
                {(rec.mercaderias || []).map((m, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 px-2">{m.orden || i + 1}</td>
                    <td className="px-2 font-mono">{m.cod_tipo_prod}</td>
                    <td className="px-2">{m.descripcion || "-"}</td>
                    <td className="px-2 text-right font-mono">{fmtKg(m.kilos)}</td>
                    <td className="px-2 text-right font-mono">{m.unidades || 0}</td>
                    <td className="px-2 text-right">{m.tropa || "-"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t font-bold">
                  <td colSpan={3} className="py-2 px-2 text-right">Total</td>
                  <td className="px-2 text-right font-mono">{fmtKg(rec.total_kilos)}</td>
                  <td className="px-2 text-right font-mono">{rec.total_unidades}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {rec.factura_numero && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
            <strong>Asociado a Factura A</strong>: {rec.factura_numero}
          </div>
        )}
        {(rec.afip_observaciones || []).length > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs">
            <strong>Observaciones AFIP:</strong>
            <pre className="mt-1 whitespace-pre-wrap">{JSON.stringify(rec.afip_observaciones, null, 2)}</pre>
          </div>
        )}
        {rec.observaciones && (
          <div className="mt-4 p-3 bg-neutral-50 border rounded text-sm">
            <strong>Observaciones:</strong> {rec.observaciones}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v, className = "" }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-neutral-500">{k}</span>
      <span className={`text-right ${className}`}>{fmt(v)}</span>
    </div>
  );
}
